import { StyledToolbarButton, StyledTooltip } from '../../../../shared/styles/components/integrations.styles';
import { useEffect, useRef, useState } from 'react';
import { useExcelExport, useExportData } from '../../../export/hooks/useExcelExport';

import { ExportProgressDialog } from '../../../export/components/ExportProgressDialog';
import React from 'react';
import { ToastService } from '../../../../services/toast.service';
import arrowDownToBase from '@ingka/ssr-icon/paths/arrow-down-to-base';

interface ExcelExportButtonProps {
    buttonText?: string;
    tooltipText?: string;
}

type ExportStage = 'fetching' | 'processing' | 'generating' | 'complete' | 'cancelling';

interface ExportState {
    progress: number;
    processedItems: number;
    totalItems: number;
    processedRows: number;
    totalRows: number;
    stage: ExportStage;
    currentFile?: number;
    totalFiles?: number;
    showProgress: boolean;
}

const initialState: ExportState = {
    progress: 0,
    processedItems: 0,
    totalItems: 0,
    processedRows: 0,
    totalRows: 0,
    stage: 'fetching',
    showProgress: false,
};

const FETCH_PROGRESS_WEIGHT = 30;
const PROCESS_PROGRESS_WEIGHT = 70;
const COMPLETION_HIDE_DELAY = 1000;

export const ExcelExportButton: React.FC<ExcelExportButtonProps> = ({
    buttonText = 'Export',
    tooltipText = 'Export all filtered results to Excel',
}) => {
    const [state, setState] = useState<ExportState>(initialState);
    const abortControllerRef = useRef<AbortController | null>(null);
    const completionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
    const isCancellingRef = useRef(false);

    useEffect(() => {
        return () => {
            abortControllerRef.current?.abort();
            if (completionTimeoutRef.current) {
                clearTimeout(completionTimeoutRef.current);
            }
        };
    }, []);

    const updateStateIfNotCancelling = (updater: (prev: ExportState) => ExportState) => {
        if (isCancellingRef.current) return;
        setState(prev => {
            if (isCancellingRef.current || prev.stage === 'cancelling') return prev;
            return updater(prev);
        });
    };

    const { getAllFilteredExportData, isLoadingAll, exportFilename } = useExportData({
        onBatchProgress: (batchNumber, fetched) => {
            const fetchProgress = Math.min(
                Math.round(FETCH_PROGRESS_WEIGHT * (1 - 1 / (batchNumber + 1))),
                FETCH_PROGRESS_WEIGHT - 1
            );

            updateStateIfNotCancelling(prev => ({
                ...prev,
                processedItems: fetched,
                totalItems: fetched,
                progress: fetchProgress,
            }));
        },
        onFetchComplete: (totalFetched: number) => {
            updateStateIfNotCancelling(prev => ({
                ...prev,
                processedItems: totalFetched,
                totalItems: totalFetched,
                progress: FETCH_PROGRESS_WEIGHT,
            }));
        },
    });

    const { exportData, isExporting } = useExcelExport({
        getData: async () => {
            if (abortControllerRef.current?.signal.aborted) {
                throw new Error('Export cancelled');
            }
            const data = await getAllFilteredExportData(abortControllerRef.current?.signal);

            updateStateIfNotCancelling(prev => ({
                ...prev,
                totalRows: data.length,
            }));

            return data;
        },
        filename: exportFilename,
        includeTimestamp: true,
        getSignal: () => abortControllerRef.current?.signal,
        onProgress: (progress) => {
            const processProgress = Math.round(progress.percentage * (PROCESS_PROGRESS_WEIGHT / 100));
            const calculatedProgress = FETCH_PROGRESS_WEIGHT + processProgress;

            updateStateIfNotCancelling(prev => ({
                ...prev,
                progress: Math.min(calculatedProgress, 99),
                processedRows: progress.processed,
                totalRows: progress.total,
                stage: 'processing',
            }));
        },
        onFileProgress: (current, total) => {
            updateStateIfNotCancelling(prev => ({
                ...prev,
                currentFile: current,
                totalFiles: total,
                stage: current > 0 ? 'generating' : prev.stage,
            }));
        },
        onError: (error) => {
            const errorMessage = error.message;

            if (errorMessage === 'Export cancelled') {
                ToastService.warning('Export cancelled');
            } else if (errorMessage.includes('limit')) {
                ToastService.error(`Export failed: ${errorMessage}`, false);
            } else if (errorMessage.includes('No items')) {
                ToastService.warning(errorMessage);
            } else {
                ToastService.error(`Export failed: ${errorMessage}`, false);
            }

            isCancellingRef.current = false;
            setState(initialState);
        },
        onSuccess: () => {
            let summary = '';

            setState(prev => {
                if (prev.stage === 'complete') return prev;

                const { totalItems, totalRows, totalFiles } = prev;
                summary = totalFiles && totalFiles > 1
                    ? `${totalItems.toLocaleString()} items exported to ${totalRows.toLocaleString()} rows across ${totalFiles} files`
                    : `${totalItems.toLocaleString()} items exported to ${totalRows.toLocaleString()} rows`;

                return {
                    ...prev,
                    progress: 100,
                    stage: 'complete',
                };
            });

            if (summary) {
                ToastService.success(summary);
            }

            completionTimeoutRef.current = setTimeout(() => {
                isCancellingRef.current = false;
                setState(initialState);
                completionTimeoutRef.current = null;
            }, COMPLETION_HIDE_DELAY);
        },
    });

    const isLoading = isExporting || isLoadingAll;

    const handleExport = async () => {
        if (isLoading) return;

        isCancellingRef.current = false;
        abortControllerRef.current = new AbortController();
        setState({ ...initialState, showProgress: true, stage: 'fetching' });

        try {
            await exportData();
        } catch (error) {
            if (error instanceof Error && error.message === 'Export cancelled') {
                return;
            }
            console.error('Export failed:', error);
        }
    };

    const handleCancel = () => {
        isCancellingRef.current = true;
        setState(prev => ({ ...prev, stage: 'cancelling' }));
        abortControllerRef.current?.abort();
    };

    return (
        <>
            <StyledTooltip title={isLoading ? 'Exporting...' : tooltipText}>
                <span>
                    <StyledToolbarButton
                        ssrIcon={arrowDownToBase}
                        text={buttonText}
                        iconOnly={true}
                        small={true}
                        onClick={handleExport}
                        disabled={isLoading}
                        aria-label={buttonText}
                    />
                </span>
            </StyledTooltip>

            <ExportProgressDialog
                isOpen={state.showProgress}
                progress={state.progress}
                processedItems={state.processedItems}
                totalItems={state.totalItems}
                processedRows={state.processedRows}
                totalRows={state.totalRows}
                stage={state.stage}
                currentFile={state.currentFile}
                totalFiles={state.totalFiles}
                onCancel={handleCancel}
            />
        </>
    );
};